import React, { Suspense, useContext, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import DataTypePill, {
  DataTypePillType,
} from 'app/components/CommonStyled/DataTypePill/DataTypePill';
import { HomeBaseDrawerWrapper } from 'app/components/CommonStyled/HomeDrawers/HomeBaseDrawer/styled';
import PersonAvatar from 'app/components/CommonStyled/PersonAvatar';
import SideDrawer from 'app/components/CommonStyled/SideDrawer';
import {
  IndustryIcon,
  ClientIcon,
  LocationIcon,
  PulseLeadIcon,
} from 'app/components/DetailsPages/shared/CommonGround/Icons';
import { DetailsPageContext } from 'app/components/DetailsPages/shared/DetailsPageContext';
import { SkeletonItem } from 'app/components/DetailsPages/shared/styled';
import CollectiveDirectoryPersonDrawer from 'app/components/People/PeopleDrawer';
import {
  DisciplineIcon,
  KeyIcon,
  SearchIconSmall,
  Sparkle,
  SubsidiaryIcon,
} from 'app/components/shared/icons';
import Calendar from 'app/components/shared/icons/calendar';
import useBehavioral from 'hooks/useBehavioral';
import { BEHAVIORALS } from 'hooks/useBehavioral';
import useUserData from 'hooks/useUserData';
import {
  contextSelector,
  groupedFiltersSelector,
  PEOPLE_CONTEXT,
  previousFiltersLengthSelector,
  scopeSelector,
} from 'store/reducers/people/people-reducer';
import { arrayToOxford, experienceMonthsToYears, yearsFloatToHumanReadable } from 'utils';

import {
  NewPersonCardAvailabilityContainer,
  NewPersonCardAvailabilityText,
  NewPersonCardHeader,
  NewPersonCardInformationItem,
  NewPersonCardInformationItemLabel,
  NewPersonCardInformationWrapper,
  NewPersonCardName,
  NewPersonCardPillsWrapper,
  NewPersonCardQuickViewButton,
  NewPersonCardRole,
  NewPersonCardSparkleContainer,
  NewPersonCardWrapper,
  NewPersonCardExperienceItem,
  NewPersonCardExperienceItemIcon,
  NewPersonCardExperienceItemLabel,
  NewPersonCardMatchesWrapper,
  NewPersonCardMatchesContainer,
  NewPersonCardPulseLead,
  NewPersonCardPillsContainer,
  NewPersonCardPillsLabel,
} from './styled';

export interface IPersonCard {
  full_name: string;
  slug: string;
  uuid: string;
  role_name: string;
  level_name: string;
  company_name: string;
  company_slug: string;
  avatar: string;
  avatar_variants: {
    [key: string]: string;
  };
  city: string;
  connection_count: number;
  experience?: {
    name: string;
    type: DataTypePillType;
    uuid: string;
    score: number;
    project_status: {
      latest_end_date: string;
      has_active_project: boolean;
    };
  }[];
  matches?: number;
  matched_detail: {
    uuid: string;
    name: string;
    type: DataTypePillType;
    group: string;
  }[];
  matched_elements?: string[];
  linkedin?: string;
  slack?: string;
  email?: string;
  cross_kyu_teaming?: boolean;
  is_freelancer?: boolean;
  disciplines?: {
    name: string;
    uuid: string;
  }[];
  tenure?: number;
  work_experience?: number;
  groups_matched?: string[];
  pulse?: {
    lead: boolean;
    description: string;
  };
}

interface PersonCardProps {
  person: IPersonCard;
}

interface ExperienceBreakdownProps {
  experience: any;
  company_name: string;
}

const ExperienceBreakdown: React.FC<ExperienceBreakdownProps> = ({ experience, company_name }) => {
  const account_holder_experience = experience.filter(
    (experience) => experience.project_status.account_holder
  );
  const client_experience = experience.filter((experience) => experience.type === 'client');
  const industry_experience = experience.filter((experience) => experience.type === 'industry');

  let account_holder_legend = null;
  if (account_holder_experience.length > 0) {
    const accountHolders = account_holder_experience
      .map((experience) => experience.project_status.account_holder)
      .flat()
      .filter((holder, index, array) => holder && array.indexOf(holder) === index);

    if (accountHolders.length <= 3 && accountHolders.length > 0) {
      account_holder_legend = 'Account holder for ' + arrayToOxford(accountHolders);
    } else {
      if (accountHolders.length > 0) {
        let toDisplay = accountHolders.slice(0, 3);
        toDisplay.push(`${accountHolders.length - 3} more`);
        account_holder_legend = `Account holder for ${arrayToOxford(toDisplay)}`;
      }
    }
  }

  let client_legend = null;
  if (client_experience.length > 0) {
    if (client_experience.length === 1) {
      if (client_experience[0].score > 0) {
        client_legend = `${experienceMonthsToYears(client_experience[0].score)} XP with ${
          client_experience[0].name
        }${
          client_experience[0].project_status.active_projects.length > 0
            ? '. Currently working with them on a project'
            : client_experience[0].project_status.latest_end_date
            ? `. Last project on ${client_experience[0].project_status.latest_end_date}`
            : ''
        }`;
      } else {
        client_legend = `Worked with ${client_experience[0].name} prior to joining ${company_name}`;
      }
    } else {
      const sorted_by_score = client_experience.sort((a, b) => b.score - a.score);
      const all_prior_experience = sorted_by_score.filter((experience) => experience.score === 0);
      if (all_prior_experience.length == sorted_by_score.length) {
        client_legend = `Worked with ${arrayToOxford(
          all_prior_experience.map((experience) => experience.name)
        )} prior to joining ${company_name}`;
      } else {
        const client_breakdown_array = [];
        sorted_by_score.forEach((experience) => {
          if (experience.score > 0) {
            client_breakdown_array.push(
              `${experienceMonthsToYears(experience.score)} XP with ${experience.name}`
            );
          } else {
            client_breakdown_array.push(
              `worked with ${experience.name} prior to joining ${company_name}`
            );
          }
        });
        client_legend = arrayToOxford(client_breakdown_array);
      }
    }
  }

  let industry_legend = null;
  if (industry_experience.length > 0) {
    if (industry_experience.length === 1) {
      if (industry_experience[0].score > 0) {
        industry_legend = `${experienceMonthsToYears(industry_experience[0].score)} XP in ${
          industry_experience[0].name
        }`;
      } else {
        industry_legend = `Worked in ${industry_experience[0].name} prior to joining ${company_name}`;
      }
    } else {
      const sorted_by_score = industry_experience.sort((a, b) => b.score - a.score);
      const all_prior_experience = sorted_by_score.filter((experience) => experience.score === 0);
      if (all_prior_experience.length == sorted_by_score.length) {
        industry_legend = `Worked in ${arrayToOxford(
          all_prior_experience.map((experience) => experience.name)
        )} prior to joining ${company_name}`;
      } else {
        const industry_breakdown_array = [];
        sorted_by_score.forEach((experience) => {
          if (experience.score > 0) {
            industry_breakdown_array.push(
              `${experienceMonthsToYears(experience.score)} XP in ${experience.name}`
            );
          } else {
            industry_breakdown_array.push(
              `${industry_breakdown_array.length == 0 ? 'Worked' : 'worked'} in ${
                experience.name
              } prior to joining ${company_name}`
            );
          }
        });
        industry_legend = arrayToOxford(industry_breakdown_array);
      }
    }
  }

  return (
    <>
      {account_holder_legend !== null ? (
        <NewPersonCardExperienceItem>
          <NewPersonCardExperienceItemLabel>
            <NewPersonCardExperienceItemIcon type="account_holder">
              <KeyIcon width={14} height={14} fill="black" />
            </NewPersonCardExperienceItemIcon>
            {account_holder_legend}
          </NewPersonCardExperienceItemLabel>
        </NewPersonCardExperienceItem>
      ) : null}
      {client_legend ? (
        <NewPersonCardExperienceItem>
          <NewPersonCardExperienceItemLabel>
            <NewPersonCardExperienceItemIcon type="client">
              <ClientIcon width={14} height={14} fill="rgba(16, 77, 91, 1)" />
            </NewPersonCardExperienceItemIcon>
            {client_legend}
          </NewPersonCardExperienceItemLabel>
        </NewPersonCardExperienceItem>
      ) : null}
      {industry_legend ? (
        <NewPersonCardExperienceItem>
          <NewPersonCardExperienceItemLabel>
            <NewPersonCardExperienceItemIcon type="industry">
              <IndustryIcon width={14} height={14} fill="rgba(141, 167, 108, 1)" />
            </NewPersonCardExperienceItemIcon>
            {industry_legend}
          </NewPersonCardExperienceItemLabel>
        </NewPersonCardExperienceItem>
      ) : null}
    </>
  );
};

export default function NewPersonCard({ person }: PersonCardProps) {
  const context = useSelector(contextSelector);
  const filtersLength = useSelector(previousFiltersLengthSelector);
  const groupedFilters = useSelector(groupedFiltersSelector);
  const detailsPageContext = useContext(DetailsPageContext);
  const scope = useSelector(scopeSelector);
  const [hovering, setHovering] = useState<boolean>(false);
  const [showDrawer, setShowDrawer] = useState<boolean>(false);
  const personCardClickedBehavioralFunction = useBehavioral(
    BEHAVIORALS.COLLECTIVE_DIRECTORY_PERSON_CARD_CLICKED
  );
  const personCardTeamingClickedBehavioralFunction = useBehavioral(
    BEHAVIORALS.TEAMING_PERSON_CARD_CLICKED
  );

  const handleClick = (e) => {
    e.preventDefault();
    if (context === PEOPLE_CONTEXT.COLLECTIVE_DIRECTORY) {
      personCardClickedBehavioralFunction({ related_object_uuid: person.uuid });
    } else {
      personCardTeamingClickedBehavioralFunction({ related_object_uuid: person.uuid });
    }
    setShowDrawer(true);
  };

  const userData = useUserData();

  const isPerfectMatch = useMemo<boolean>(() => {
    if (!person.groups_matched) return false;

    return Object.keys(groupedFilters).every((filter) => person.groups_matched?.includes(filter));
  }, [person?.groups_matched, groupedFilters]);

  const handleMouseEnter = () => {
    setHovering(true);
  };

  const handleMouseLeave = () => {
    setHovering(false);
  };

  const finalMatches = useMemo(() => {
    let matches = [];

    if (person.matched_detail) {
      matches = [...person.matched_detail];
    }

    if (
      detailsPageContext?.data?.uuid &&
      detailsPageContext?.model &&
      detailsPageContext?.data?.name
    ) {
      const scopeMatch = {
        uuid: detailsPageContext?.data?.uuid,
        name: detailsPageContext?.data?.name,
        type: detailsPageContext?.model,
      };

      matches.push(scopeMatch);
    }

    return matches;
  }, [person.matched_detail, detailsPageContext]);

  return (
    <>
      <Link
        to={`/person/${person.slug}`}
        onClick={handleClick}
        style={{ textDecoration: 'none', display: 'flex' }}
      >
        <NewPersonCardWrapper
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          data-hovering={hovering}
        >
          <PersonAvatar
            avatar={person?.avatar_variants?.lg}
            name={person.full_name}
            slug={person.slug}
            size={92}
            disablePropagation={true}
            onClick={() => {}}
            highlighted
          >
            {isPerfectMatch ? (
              <NewPersonCardSparkleContainer>
                <Sparkle width={24} height={24} fill="var(--color-data-people-foreground)" />
              </NewPersonCardSparkleContainer>
            ) : null}
          </PersonAvatar>
          <NewPersonCardQuickViewButton>Quick View</NewPersonCardQuickViewButton>
          <NewPersonCardHeader>
            <NewPersonCardName>{person.full_name}</NewPersonCardName>
            {context === PEOPLE_CONTEXT.TEAMING ? (
              <>
                {person.company_name !== userData.company_name && (
                  <NewPersonCardAvailabilityContainer available={person.cross_kyu_teaming}>
                    <NewPersonCardAvailabilityText>
                      {person.cross_kyu_teaming ? 'Eligible' : 'Not Eligible'}
                    </NewPersonCardAvailabilityText>
                  </NewPersonCardAvailabilityContainer>
                )}
              </>
            ) : null}
            {person.role_name && person.company_name ? (
              <>
                <NewPersonCardRole>
                  {person.role_name} at {person.company_name + ' '}
                  {person.is_freelancer ? (
                    <SubsidiaryIcon
                      width={16}
                      height={16}
                      fill="var(--color-data-company-foreground)"
                    />
                  ) : null}
                  {person.pulse.lead == true ? (
                    <>
                      |
                      <NewPersonCardPulseLead>
                        {' '}
                        Pulse Lead <PulseLeadIcon width={20} height={20} fill="var(--color-text)" />
                      </NewPersonCardPulseLead>
                    </>
                  ) : null}
                </NewPersonCardRole>
              </>
            ) : null}
          </NewPersonCardHeader>
          {person.experience?.length > 0 ? (
            <ExperienceBreakdown
              experience={person.experience}
              company_name={person.company_name}
            />
          ) : null}
          {/* This changed again: https://app.asana.com/1/1200556643465433/project/1206392526522074/task/1211032338079096?focus=true */}
          {person.matched_detail && person.experience?.length == 0 && (
            <NewPersonCardPillsWrapper data-id="people-matched-detail-pills">
              {person.matched_detail?.slice(0, 3).map((element) => (
                <NewPersonCardPillsContainer>
                  <SearchIconSmall fill={'rgba(23, 28, 51, 0.5)'} width={15} height={15} />
                  <NewPersonCardPillsLabel>{element.name}</NewPersonCardPillsLabel>
                </NewPersonCardPillsContainer>
              ))}
              {person.matched_detail?.length > 3 && (
                <DataTypePill
                  type={'more'}
                  title={`...${person.matched_detail?.length - 3} more`}
                  clickable={false}
                />
              )}
            </NewPersonCardPillsWrapper>
          )}
          <NewPersonCardInformationWrapper
            variants={{
              hidden: { maxHeight: 0, opacity: 0 },
              visible: { maxHeight: 500, opacity: 1 },
            }}
            initial="hidden"
            animate={hovering ? 'visible' : 'hidden'}
          >
            {person.city ? (
              <NewPersonCardInformationItem>
                <NewPersonCardInformationItemLabel>
                  <LocationIcon width={16} height={16} fill="var(--color-text)" />
                  {person.city}
                </NewPersonCardInformationItemLabel>
              </NewPersonCardInformationItem>
            ) : null}
            {person.disciplines && person.disciplines.length > 0 ? (
              <NewPersonCardInformationItem>
                <NewPersonCardInformationItemLabel>
                  <DisciplineIcon width={16} height={16} fill="var(--color-text)" />
                  {person.disciplines.map((discipline) => discipline.name).join(', ')}
                </NewPersonCardInformationItemLabel>
              </NewPersonCardInformationItem>
            ) : null}
            {person.work_experience && person.experience?.length == 0 ? (
              <NewPersonCardInformationItem>
                <NewPersonCardInformationItemLabel>
                  <Sparkle width={16} height={16} fill="var(--color-text)" />
                  {yearsFloatToHumanReadable(person.work_experience)} of experience
                </NewPersonCardInformationItemLabel>
              </NewPersonCardInformationItem>
            ) : null}
            {person.tenure && person.experience?.length == 0 ? (
              <NewPersonCardInformationItem>
                <NewPersonCardInformationItemLabel>
                  <Calendar width={16} height={16} fill="var(--color-text)" />
                  {yearsFloatToHumanReadable(person.tenure)} at {person.company_name}
                </NewPersonCardInformationItemLabel>
              </NewPersonCardInformationItem>
            ) : null}
          </NewPersonCardInformationWrapper>
          {person.matched_detail?.length > 0 && person.experience?.length > 0 && (
            <NewPersonCardMatchesWrapper>
              <NewPersonCardMatchesContainer>
                <NewPersonCardPillsWrapper>
                  {person.matched_detail
                    ?.filter(
                      (match, index, self) => index === self.findIndex((m) => m.name === match.name)
                    )
                    .map((match) => (
                      <NewPersonCardPillsContainer key={match.uuid}>
                        <SearchIconSmall fill={'rgba(23, 28, 51, 0.5)'} width={15} height={15} />
                        <NewPersonCardPillsLabel>{match.name}</NewPersonCardPillsLabel>
                      </NewPersonCardPillsContainer>
                    ))}
                </NewPersonCardPillsWrapper>
              </NewPersonCardMatchesContainer>
            </NewPersonCardMatchesWrapper>
          )}
        </NewPersonCardWrapper>
      </Link>
      {showDrawer ? (
        <Suspense fallback={<HomeBaseDrawerWrapper />}>
          <SideDrawer closeDrawer={() => setShowDrawer(false)} withClose>
            <CollectiveDirectoryPersonDrawer
              slug={person.slug}
              uuid={person.uuid}
              context={context}
              matches={finalMatches}
              fromGlobalDrawer
            />
          </SideDrawer>
        </Suspense>
      ) : null}
    </>
  );
}

NewPersonCard.Skeleton = () => {
  return (
    <NewPersonCardWrapper>
      <SkeletonItem animated style={{ height: 92, width: 92, borderRadius: '100%' }} />
      <SkeletonItem animated style={{ height: 35, width: '100%' }} />
      <SkeletonItem animated style={{ height: 35, width: '100%' }} />
      <SkeletonItem animated style={{ height: 35, width: '100%' }} />
      <SkeletonItem animated style={{ height: 35, width: '100%' }} />
    </NewPersonCardWrapper>
  );
};
